/**
 * Pick where a Hide and Seek round is played: an open-world area (grouped by
 * planet) and, optionally, one zone map in it. Shows the chosen map so the admin
 * can see what players will be drawn on.
 */

import type { GameLocation } from '@swtorbit/shared-socket-events';
import { GameMap } from '@swtorbit/shared-components';
import { usePlatformAreas } from '../../hooks/usePlatformAreas';
import { usePlatformAreaMaps } from '../../hooks/usePlatformAreaMaps';

interface HnsLocationPickerProps {
  value: GameLocation | null;
  onChange: (location: GameLocation | null) => void;
  disabled?: boolean;
  /** Prefix for the inputs' ids, so several pickers can share a page. */
  idPrefix: string;
}

export function HnsLocationPicker({ value, onChange, disabled, idPrefix }: HnsLocationPickerProps) {
  const { planets, loading, error } = usePlatformAreas();
  const areaMaps = usePlatformAreaMaps(value?.logAreaId ?? null);
  const zone = areaMaps.maps?.zones.find((z) => z.id === value?.zoneMapId) ?? null;
  const shownMap = zone ?? areaMaps.maps?.world ?? null;

  if (error) return <p className="fs-sm text-red">{error}</p>;

  return (
    <div className="d-flex flex-column gap-2xs">
      <div className="d-flex gap-xs">
        <select
          className="select"
          id={`${idPrefix}-area`}
          aria-label="Area"
          value={value?.logAreaId ?? ''}
          disabled={disabled || loading}
          onChange={(e) => onChange(e.target.value ? { logAreaId: Number(e.target.value), zoneMapId: null } : null)}
        >
          <option value="">{loading ? 'Loading areas…' : 'Choose an area…'}</option>
          {planets.map((planet) => (
            <optgroup key={planet.planetName} label={planet.planetName}>
              {planet.areas.map((area) => (
                <option key={area.logAreaId} value={area.logAreaId}>
                  {area.name}
                  {area.galaxyFaction && area.galaxyFaction !== 'both' ? ` (${area.galaxyFaction})` : ''}
                </option>
              ))}
            </optgroup>
          ))}
        </select>

        {value && (
          <select
            className="select"
            id={`${idPrefix}-zone`}
            aria-label="Zone"
            value={value.zoneMapId ?? ''}
            disabled={disabled || areaMaps.loading}
            onChange={(e) => onChange({ logAreaId: value.logAreaId, zoneMapId: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">Whole planet</option>
            {areaMaps.maps?.zones.map((z) => (
              <option key={z.id} value={z.id}>
                {z.name ?? `Map ${z.id}`}
              </option>
            ))}
          </select>
        )}
      </div>

      {areaMaps.error && <p className="fs-sm text-red">{areaMaps.error}</p>}
      {shownMap && (
        <GameMap
          imageUrl={shownMap.image.url}
          width={shownMap.image.width}
          height={shownMap.image.height}
          name={shownMap.name}
          size="small"
        />
      )}
    </div>
  );
}
